import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChakraProvider, Box, Spinner, Flex, extendTheme, useDisclosure } from '@chakra-ui/react';
// import { Global, css } from '@emotion/react'; // This line should be removed or commented out
import Navbar from './components/Navbar';
import SubscriptionGate from './components/SubscriptionGate';
import WeekReviewParentComponentV2 from './components/WeekReviewParentComponentV2';
import YearReviewParentComponent from './components/YearReviewParentComponent';
import CompareWeekV3 from './components/CompareWeekV3';
import CompareBusinessDashboard from './components/CompareBusinessDashboard';
import DashboardWrapper from './components/dashboards/DashboardWrapper';
import HomePage from './components/HomePage';
import Login from './components/Login';
import CreatePassword from './components/CreatePassword';
import ConsumerLanding from './components/ConsumerLanding';
import SmbLanding from './components/SmbLanding';
import AgencyLanding from './components/AgencyLanding';
import AgencyReviewEmbed from './components/AgencyReviewEmbed';
import MarketReportLanding from './components/MarketReportLanding';
import DemoPage from './components/DemoPage';
import FeedbackPage from './components/FeedbackPage';
import AgencyAdminDashboard from './components/AgencyAdminDashboard';
import TipNotification from './components/TipNotification';
import ChatDrawer from './components/ChatDrawer';
import ChatPage from './components/ChatPage';
import FloatingChatButton from './components/FloatingChatButton';
// Removed SpyFu component - replaced with keyword intelligence dashboard

import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { UserProvider } from './contexts/UserContext';
import { WeightsProvider } from './contexts/WeightsContext';
import { useActivityTracker } from './hooks/useActivityTracker';
import { useWhiteLabelTab } from './hooks/useWhiteLabelTab';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyA3cmeVlof6XRGr8OZrB7hNVx1T0XZKlxk",
  authDomain: "localytics-v2.firebaseapp.com",
  projectId: "localytics-v2",
  storageBucket: "localytics-v2.firebasestorage.app",
  messagingSenderId: "670450720940",
  appId: "1:670450720940:web:1f85b70eba23e3cba4ff09"
};

// Initialize Firebase
initializeApp(firebaseConfig);

// const Fonts = () => ( // Definition removed
// ...
// );

const config = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

export const theme = extendTheme({
  config,
  colors: {
    brand: {
      50: '#e6f2ff',
      100: '#cce0ff',
      200: '#99c1ff',
      300: '#66a3ff',
      400: '#3385ff',
      500: '#0066ff', // Primary brand color
      600: '#0052cc',
      700: '#003d99',
      800: '#002966',
      900: '#001433',
    },
    insight: {
      positive: '#48BB78', // Green for positive trends
      neutral: '#ECC94B',  // Yellow for neutral
      negative: '#F56565', // Red for negative trends
      info: '#4299E1',     // Blue for informational
    }
  },
  // Animation configurations for consistent micro-animations
  transition: {
    standard: "all 0.3s ease",
    fast: "all 0.15s ease",
    slow: "all 0.5s ease",
    bounce: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    elastic: "all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
  },
  animations: {
    fadeIn: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    slideUp: {
      from: { opacity: 0, transform: "translateY(20px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    pulse: {
      "0%": { transform: "scale(1)" },
      "50%": { transform: "scale(1.05)" },
      "100%": { transform: "scale(1)" },
    },
    spin: {
      "0%": { transform: "rotate(0deg)" },
      "100%": { transform: "rotate(360deg)" },
    },
  },
  fonts: {
    heading: "'Geist Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
    body: "'Geist Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
    mono: "'Geist Mono', SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: "semibold",
        borderRadius: "md",
        transition: "all 0.2s",
      },
      variants: {
        solid: (props) => ({
          bg: props.colorScheme === "blue" ? "brand.500" : undefined,
          _hover: {
            bg: props.colorScheme === "blue" ? "brand.600" : undefined,
            transform: "translateY(-2px)",
            boxShadow: "md",
          },
        }),
        outline: (props) => ({
          borderColor: props.colorScheme === "blue" ? "brand.500" : undefined,
          color: props.colorScheme === "blue" ? "brand.500" : undefined,
          _hover: {
            bg: props.colorScheme === "blue" ? "brand.50" : undefined,
            transform: "translateY(-2px)",
            boxShadow: "sm",
          },
        }),
        ghost: (props) => ({
          _hover: {
            bg: props.colorScheme === "blue" ? "brand.50" : undefined,
            transform: "translateY(-1px)",
          },
        }),
      },
    },
    Card: {
      baseStyle: {
        container: {
          borderRadius: "lg",
          overflow: "hidden",
          boxShadow: "sm",
          transition: "all 0.2s",
          _hover: {
            boxShadow: "md",
          },
        },
      },
    },
    Input: {
      baseStyle: {
        field: {
          borderRadius: "md",
          _focus: {
            boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
          },
        },
      },
      variants: {
        outline: {
          field: {
            borderColor: "gray.200",
            _hover: {
              borderColor: "blue.400",
            },
          },
        },
      },
    },
    Badge: {
      baseStyle: {
        borderRadius: "full",
        px: 2,
        py: 1,
        fontWeight: "medium",
      },
    },
    Progress: {
      baseStyle: {
        track: {
          borderRadius: "full",
        },
        filledTrack: {
          borderRadius: "full",
        },
      },
    },
    Tooltip: {
      baseStyle: {
        borderRadius: "md",
        bg: "gray.700",
        color: "white",
        px: 3,
        py: 2,
        fontWeight: "medium",
        boxShadow: "md",
        fontSize: "sm",
      },
    },
    // Customize tables
    Table: {
      variants: {
        simple: {
          th: {
            borderColor: "gray.200",
            fontSize: "sm",
            fontWeight: "semibold",
            letterSpacing: "wider",
          },
          td: {
            borderColor: "gray.100",
          },
        },
      },
    },
    // Custom component styles for enhanced UI
    Select: {
      baseStyle: {
        field: {
          borderRadius: "md",
        },
      },
      variants: {
        outline: {
          field: {
            borderColor: "gray.200",
            _hover: {
              borderColor: "blue.400",
            },
            _focus: {
              borderColor: "blue.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
            },
          },
        },
      },
    },
  },
  styles: {
    global: {
      'html, body': {
        minHeight: '100vh',
        margin: 0,
        padding: 0,
        WebkitTapHighlightColor: 'transparent',
        backgroundColor: 'gray.50',
      },
      '#root': {
        width: '100%',
        minHeight: '100vh',
        // Restore desktop scale that was in place before mobile PR
        '@media (min-width: 48em)': {
          transform: 'scale(0.75)',
          transformOrigin: 'top left',
          width: '133.33%',
          minHeight: '133.33vh',
        },
      },
      '*:focus': {
        outline: 'none',
      },
      // Add smooth transitions for hover effects
      'a, button, input, select': {
        transition: 'all 0.2s ease-in-out',
      },
      // Improve touch targets on mobile (only for interactive standalone elements)
      'button:not([class*="ModalCloseButton"]), a': {
        minHeight: { base: '44px', md: '36px' },
      },
      // Prevent text size adjustment on orientation change
      body: {
        WebkitTextSizeAdjust: '100%',
      }
    }
  }
});

// Activity tracker component - tracks active time when user is authenticated
function ActivityTracker() {
  useActivityTracker();
  return null;
}

function WhiteLabelTabBranding() {
  useWhiteLabelTab();
  return null;
}

// /landing is a static Next.js export merged into the production build. A full
// document navigation is required so Firebase serves that file instead of the SPA.
function LandingStaticRedirect() {
  useEffect(() => {
    window.location.replace('/landing/');
  }, []);

  return (
    <Flex justify="center" align="center" h="100vh">
      <Spinner size="xl" />
    </Flex>
  );
}

function PrivateRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return isAuthenticated ? <SubscriptionGate>{children}</SubscriptionGate> : <Navigate to="/login" />;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [showTip, setShowTip] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [initialChatMessage, setInitialChatMessage] = useState('');
  const { isOpen: isChatOpen, onOpen: onChatOpen, onClose: onChatClose } = useDisclosure();

  // Handler to open chat with an initial message
  const openChatWithMessage = (message) => {
    setInitialChatMessage(message);
    onChatOpen();
  };

  // Clear initial message when chat closes
  const handleChatClose = () => {
    setInitialChatMessage('');
    onChatClose();
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setCurrentUser(user);
      setIsLoadingAuth(false);
      // Show tip only once after login, for example
      if (user && !localStorage.getItem('tipShown')) {
        // setShowTip(true); // Disabled for now
        // localStorage.setItem('tipShown', 'true');
      }
    });
    return () => unsubscribe();
  }, []);

  if (isLoadingAuth) {
    return (
      <Flex justify="center" align="center" height="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <ChakraProvider theme={theme}>
      <UserProvider>
        <WeightsProvider>
        {/* <Fonts /> */}
        <Box minH="100vh" bg="gray.50">
          <Routes>
          <Route 
            path="/" 
            element={
              isAuthenticated ? <Navigate to="/home" /> : <ConsumerLanding />
            } 
          />
          <Route path="/smb" element={<SmbLanding />} />
          <Route path="/agencies" element={<AgencyLanding />} />
          <Route path="/agencies/review-demo" element={<AgencyReviewEmbed />} />
          <Route path="/market-report" element={<MarketReportLanding />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/create-password" element={<CreatePassword />} />
          <Route path="/landing/*" element={<LandingStaticRedirect />} />
          <Route
            path="/*"
            element={
              isAuthenticated ? (
                <PrivateRoute>
                  <Box>
                    <ActivityTracker />
                    <WhiteLabelTabBranding />
                    <Navbar isAuthenticated={isAuthenticated} currentUser={currentUser} onChatOpen={onChatOpen} />
                    {showTip && <TipNotification onClose={() => setShowTip(false)} />}
                    <Box
                      as="main"
                      ml={{ base: 0, md: "72px" }}
                      pb={{ base: "70px", md: 0 }}
                      transition="margin-left 0.2s"
                      p={{ base: 2, md: 4 }}
                    >
                      <Routes>
                        <Route path="/home" element={<HomePage onOpenChat={openChatWithMessage} />} />
                        <Route 
                          path="/dashboards/total-reviews" 
                          element={<DashboardWrapper type="total-reviews" />} 
                        />
                        <Route 
                          path="/dashboards/gross-growth" 
                          element={<DashboardWrapper type="gross-growth" />} 
                        />
                        <Route 
                          path="/dashboards/sentiment" 
                          element={<DashboardWrapper type="sentiment" />} 
                        />
                        <Route 
                          path="/dashboards/market-trends" 
                          element={<DashboardWrapper type="market-trends" />} 
                        />
                        <Route 
                          path="/dashboards/web-traffic" 
                          element={<DashboardWrapper type="web-traffic" />} 
                        />
                        <Route 
                          path="/dashboards/business-insights" 
                          element={<DashboardWrapper type="business-insights" />} 
                        />
                        <Route 
                          path="/dashboards/revenue-estimation" 
                          element={<DashboardWrapper type="revenue-estimation" />} 
                        />
                        <Route 
                          path="/dashboards/revenue-opportunity" 
                          element={<DashboardWrapper type="revenue-opportunity" />} 
                        />
                        <Route
                          path="/dashboards/market-intelligence"
                          element={<DashboardWrapper type="market-intelligence" />}
                        />
                        <Route
                          path="/dashboards/ads-intelligence"
                          element={<DashboardWrapper type="ads-intelligence" />}
                        />
                        <Route
                          path="/playbook/goals" 
                          element={<DashboardWrapper type="playbook-goals" />} 
                        />
                        <Route 
                          path="/playbook/actions" 
                          element={<DashboardWrapper type="playbook-actions" />} 
                        />
                        <Route 
                          path="/playbook/reading" 
                          element={<DashboardWrapper type="playbook-reading" />} 
                        />
                        <Route 
                          path="/metrics" 
                          element={<DashboardWrapper type="metrics-insights" />} 
                        />
                        <Route 
                          path="/week-in-review" 
                          element={<WeekReviewParentComponentV2 />} 
                        />
                        <Route 
                          path="/year-in-review" 
                          element={<YearReviewParentComponent />} 
                        />
                        <Route 
                          path="/compare-week" 
                          element={<CompareWeekV3 initialView="weeks" />} 
                        />
                        <Route 
                          path="/compare-year" 
                          element={<CompareBusinessDashboard />} 
                        />
                        <Route 
                          path="/roadmap" 
                          element={<FeedbackPage />} 
                        />
                        <Route
                          path="/dashboards/keyword-intelligence"
                          element={<DashboardWrapper type="keyword-intelligence" />}
                        />
                        <Route
                          path="/dashboards/favorites"
                          element={<DashboardWrapper type="favorites" />}
                        />
                        <Route
                          path="/dashboards/localytics-score"
                          element={<DashboardWrapper type="localytics-score" />}
                        />
                        <Route
                          path="/dashboards/pricing"
                          element={<DashboardWrapper type="pricing" />}
                        />
                        <Route
                          path="/dashboards/content-templates"
                          element={<DashboardWrapper type="content-templates" />}
                        />
                        <Route
                          path="/dashboards/keywords"
                          element={<DashboardWrapper type="keywords" />}
                        />
                        <Route
                          path="/dashboards/spyfu-keywords"
                          element={<DashboardWrapper type="spyfu-keywords" />}
                        />
                        <Route
                          path="/dashboards/organic-paid-traffic"
                          element={<DashboardWrapper type="organic-paid-traffic" />}
                        />
                        <Route
                          path="/dashboards/competitor-map"
                          element={<DashboardWrapper type="competitor-map" />}
                        />
                        <Route path="/agency/admin" element={<AgencyAdminDashboard />} />
                        <Route path="/chat" element={<ChatPage />} />
                      </Routes>
                    </Box>
                    <ChatDrawer isOpen={isChatOpen} onClose={handleChatClose} initialMessage={initialChatMessage} />
                    <FloatingChatButton onOpenChat={openChatWithMessage} />
                  </Box>
                </PrivateRoute>
              ) : (
                <Navigate to="/" />
              )
            }
          />
        </Routes>
      </Box>
        </WeightsProvider>
      </UserProvider>
    </ChakraProvider>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}